// SUB-OFFICE 7 — behavior-log envelope. One schema, two consumers: the client
// recorder emits it, the worker verifier validates it, and NOTHING in it is
// trusted as judgment — client-computed values are display-only everywhere.
// The server recomputes every metric from seed + timestamps or full replay.

export const LOG_VERSION = 1;
export const MAX_EVENTS = 800;
export const MAX_RAW_BYTES = 32768;
export const ROUTE_SPEED_CAP_MPS = 4.0; // hard teleport line (walk max 2.6 + slack)
export const WALL_CLOCK_SLACK_MS = 8000;

export interface LogEvent {
  t: number; // ms from t0, strictly increasing
  e: string; // event id from the system's vocabulary
  [key: string]: unknown;
}

export interface RouteData {
  pts: Array<[number, number, number]>; // [x, y, t_ms] — 250ms base, 0.25m quantized
  len_m?: number;
  still_ms?: number;
}

export interface BehaviorLog {
  v: number;
  system: string;
  task_id: string | null; // null only for FORM00 (session-seeded, no task row)
  t0: number; // ms epoch anchor (client-echoed)
  events: LogEvent[];
  route?: RouteData;
  summary?: Record<string, unknown>;
}

/** Per-system event vocabularies. Unknown events invalidate the log. */
export const EVENT_VOCAB: Record<string, readonly string[]> = {
  FORM5J: ['canister_opened', 'excerpt_dwell', 'reread', 'stamp', 'streak'],
  FORM2V: ['mounted', 'bleed_start', 'bleed_end', 'feed_tap', 'focus', 'complete', 'abandoned'],
  FORM33C: [
    'station_arrive',
    'station_dwell',
    'deviation',
    'pace_violation',
    'haste_attempt',
    'yield',
    'circuit_complete',
  ],
  FORM00: ['enter_room0', 'lever_dwell', 'pull', 'reset', 'exit', 'declined'],
  // ---- W2 ----
  FORM8R: [
    'mounted',
    'canister_arrived',
    'lever',
    'timeout_shred',
    'misroute_returned',
    'latency_probe',
    'shift_complete',
    'abandoned',
  ],
  FORM5D: [
    'placard_dwell',
    'doc_shown',
    'field_circled',
    'circle_erased',
    'loupe',
    'stamp',
    'amendment_shown',
    'shift_abandoned',
  ],
  FORM5M: [
    'picked_up',
    'delivered',
    'jolt',
    'drip',
    'lamp_observed',
    'haste_zone',
    'line_cross',
    'bump',
    'jumper_beat',
  ],
  FORM5S: [
    'directive_dwell',
    'doc_read',
    'reread',
    'shred_start',
    'shred_complete',
    'shred_aborted',
    'retained',
    'hover_over_teeth',
    'hatch_attempt',
  ],
  FORM4T: [
    'ticket_opened',
    'fix_started',
    'cable_pulled',
    'cable_seated',
    'knob_hold',
    'knob_release',
    'slam',
    'reboot_caused',
    'kill_tap',
    'ticket_closed',
    'abandoned',
    'grievance',
  ],
  FORM12K: ['mounted', 'dial_hold', 'dial_release', 'test_slam', 'certify', 'dismount_abandoned'],
  FORM44S: [
    'hold_start',
    'beat_fired',
    'prompt_shown',
    'drift',
    'excursion',
    'reentry',
    'interact_press',
    'hold_end',
  ],
  FORM17O: ['subject_acquired', 'noticed', 'act_window', 'deposition'],
};

export interface EnvelopeCheck {
  ok: boolean;
  reason?: string;
}

export interface EnvelopeBounds {
  system: string;
  issuedAt: number; // physical_tasks.issued_at (or seeding time for FORM00)
  now: number;
  minDurationMs?: number; // impossible-speed floor per system
  maxDurationMs?: number;
}

/**
 * Envelope validation — shape, caps, monotonicity, wall-clock bounds, route
 * plausibility. Rejections are structural; scoring judgments live per-system.
 */
export function validateEnvelope(log: unknown, bounds: EnvelopeBounds): EnvelopeCheck {
  if (!log || typeof log !== 'object') return { ok: false, reason: 'not an object' };
  const l = log as BehaviorLog;
  if (l.v !== LOG_VERSION) return { ok: false, reason: 'unknown log version' };
  if (l.system !== bounds.system) return { ok: false, reason: 'system mismatch' };
  if (!Array.isArray(l.events)) return { ok: false, reason: 'events missing' };
  if (l.events.length === 0) return { ok: false, reason: 'no events' };
  if (l.events.length > MAX_EVENTS) return { ok: false, reason: 'event cap exceeded' };

  let raw: string;
  try {
    raw = JSON.stringify(log);
  } catch {
    return { ok: false, reason: 'unserializable' };
  }
  if (raw.length > MAX_RAW_BYTES) return { ok: false, reason: 'raw size cap exceeded' };

  const vocab = EVENT_VOCAB[bounds.system];
  if (!vocab) return { ok: false, reason: 'unknown system' };

  let last = -1;
  for (const ev of l.events) {
    if (!ev || typeof ev.t !== 'number' || !isFinite(ev.t) || ev.t < 0) {
      return { ok: false, reason: 'event timestamp invalid' };
    }
    if (typeof ev.e !== 'string' || !vocab.includes(ev.e)) {
      return { ok: false, reason: `unknown event '${String((ev as LogEvent).e)}'` };
    }
    if (ev.t <= last) return { ok: false, reason: 'timestamps not strictly increasing' };
    last = ev.t;
  }

  // duration vs wall clock: the log cannot claim more time than existed
  const logDuration = last;
  const wallDuration = bounds.now - bounds.issuedAt;
  if (logDuration > wallDuration + WALL_CLOCK_SLACK_MS) {
    return { ok: false, reason: 'log duration exceeds wall clock' };
  }
  if (bounds.minDurationMs && logDuration < bounds.minDurationMs) {
    return { ok: false, reason: 'log impossibly brief' };
  }
  if (bounds.maxDurationMs && logDuration > bounds.maxDurationMs) {
    return { ok: false, reason: 'log exceeds the duty window' };
  }

  // route plausibility: numeric triples, monotonic time, no teleportation
  if (l.route) {
    if (!Array.isArray(l.route.pts) || l.route.pts.length < 2) {
      return { ok: false, reason: 'route malformed' };
    }
    if (l.route.pts.length > 2400) return { ok: false, reason: 'route sample cap exceeded' };
    let pt = -1;
    let prev: [number, number, number] | null = null;
    for (const p of l.route.pts) {
      if (!Array.isArray(p) || p.length < 3 || p.some((v) => typeof v !== 'number' || !isFinite(v))) {
        return { ok: false, reason: 'route point invalid' };
      }
      if (p[2] <= pt) return { ok: false, reason: 'route time not increasing' };
      if (prev) {
        const dt = (p[2] - prev[2]) / 1000;
        const d = Math.hypot(p[0] - prev[0], p[1] - prev[1]);
        if (dt > 0 && d / dt > ROUTE_SPEED_CAP_MPS) {
          return { ok: false, reason: 'route segment exceeds possible speed' };
        }
      }
      pt = p[2];
      prev = p as [number, number, number];
    }
  }
  return { ok: true };
}

/** Events of one kind, in order. */
export function eventsOf(log: BehaviorLog, e: string): LogEvent[] {
  return log.events.filter((ev) => ev.e === e);
}

/** Route duration helper (ms of last event). */
export function logDurationMs(log: BehaviorLog): number {
  return log.events.length ? log.events[log.events.length - 1].t : 0;
}
